import { useEffect, useState, type ComponentType } from 'react';
import { ExternalLink } from 'lucide-react';
import { ClinicLaunchPanel } from './features/clinicLaunches/ClinicLaunchPanel';
import { isConfigValid, useAdminConfig } from './lib/adminConfig';
import { ADMIN_ROUTES, type AdminRoute, type ClinicPortalWorkspace } from './lib/routes';

const CLINIC_PORTAL_ROUTE: AdminRoute = 'clinic-launches';
const CLINIC_PORTAL_LABEL = 'Clinic Demo/Payment Portal';
const TWENTY_CRM_URL = 'https://crm.canisx.com';
const INTERNAL_ARCHIVE_URL = 'https://screendesignflows.canisx.com';

const NAV_ITEMS: { id: AdminRoute; label: string }[] = [
  { id: CLINIC_PORTAL_ROUTE, label: CLINIC_PORTAL_LABEL },
];

const PORTAL_WORKSPACE_ITEMS: { id: ClinicPortalWorkspace; label: string }[] = [
  { id: 'creation', label: 'Clinic Demo Creation' },
  { id: 'existing', label: 'Existing Clinic Demos' },
];

const ClinicLaunchPanelWithWorkspaceMode = ClinicLaunchPanel as ComponentType<{
  workspaceMode: ClinicPortalWorkspace;
}>;

export default function App() {
  const [route, setRoute] = useState<AdminRoute>(() => {
    const hash = window.location.hash.replace('#', '');
    return ADMIN_ROUTES.includes(hash as AdminRoute) ? (hash as AdminRoute) : CLINIC_PORTAL_ROUTE;
  });
  const [portalExpanded, setPortalExpanded] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState<ClinicPortalWorkspace | null>(null);
  const config = useAdminConfig();
  const [envBadge, setEnvBadge] = useState<string>('');

  useEffect(() => {
    const host = window.location.hostname;
    if (host === 'adminpanel.canisx.com') {
      setEnvBadge('prod');
    } else if (host === 'adminpanel-dev.canisx.com' || host.includes('adminpanel-dev.')) {
      setEnvBadge('dev');
    } else {
      setEnvBadge('local');
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (ADMIN_ROUTES.includes(hash as AdminRoute)) {
        setRoute(hash as AdminRoute);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleNav = (id: AdminRoute) => {
    window.location.hash = id;
    setRoute(id);
    setPortalExpanded(true);
  };

  const handleWorkspaceSelect = (workspace: ClinicPortalWorkspace) => {
    if (route !== CLINIC_PORTAL_ROUTE) {
      window.location.hash = CLINIC_PORTAL_ROUTE;
      setRoute(CLINIC_PORTAL_ROUTE);
    }
    setPortalExpanded(true);
    setWorkspaceMode(workspace);
  };

  const handlePortalBack = () => {
    setPortalExpanded(false);
  };

  if (!isConfigValid(config)) {
    return (
      <div className="config-error" role="alert">
        <h1>Admin panel configuration error</h1>
        <p>
          The admin API base URL is not configured. Set <code>VITE_CANISX_ADMIN_API_BASE_URL</code>{' '}
          in your environment and restart the app.
        </p>
        <p className="config-error-meta">
          Current API base: <code>{config.apiBaseUrl || '(missing)'}</code>
        </p>
      </div>
    );
  }

  const activeWorkspace = PORTAL_WORKSPACE_ITEMS.find((item) => item.id === workspaceMode) ?? null;

  return (
    <div className="admin-shell">
      <aside className="admin-nav" aria-label="Admin navigation">
        <div className="admin-nav-header">
          <h1>CanisX Admin</h1>
          <span className={`env-badge env-badge--${envBadge}`} data-testid="env-badge">
            {envBadge}
          </span>
        </div>
        <nav
          aria-label={portalExpanded ? `${CLINIC_PORTAL_LABEL} navigation` : 'Primary navigation'}
          data-testid="admin-navigation"
        >
          {portalExpanded && route === CLINIC_PORTAL_ROUTE ? (
            <div className="admin-nav-section">
              <p className="admin-nav-section-label">{CLINIC_PORTAL_LABEL}</p>
            </div>
          ) : null}
          <ul id="clinic-portal-nav">
            {portalExpanded && route === CLINIC_PORTAL_ROUTE
              ? PORTAL_WORKSPACE_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      className={workspaceMode === item.id ? 'active' : ''}
                      onClick={() => handleWorkspaceSelect(item.id)}
                      aria-current={workspaceMode === item.id ? 'page' : undefined}
                      data-testid={`workspace-nav-${item.id}`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))
              : NAV_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      className={route === item.id ? 'active' : ''}
                      onClick={() => handleNav(item.id)}
                      aria-current={route === item.id ? 'page' : undefined}
                      aria-expanded={route === CLINIC_PORTAL_ROUTE ? portalExpanded : undefined}
                      aria-controls={item.id === CLINIC_PORTAL_ROUTE ? 'clinic-portal-nav' : undefined}
                      data-testid={`nav-${item.id}`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
            {!portalExpanded ? (
              <li>
                <a
                  className="admin-nav-link"
                  href={TWENTY_CRM_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="nav-twenty-crm"
                >
                  Twenty CRM <span aria-hidden="true">↗</span>
                </a>
              </li>
            ) : null}
            {!portalExpanded ? (
              <li>
                <a
                  className="admin-nav-link"
                  href={INTERNAL_ARCHIVE_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="nav-internal-archive"
                >
                  Product Screens <ExternalLink size={16} aria-hidden="true" />
                </a>
              </li>
            ) : null}
            {portalExpanded && route === CLINIC_PORTAL_ROUTE ? (
              <li className="admin-nav-back-item">
                <button className="admin-nav-back" onClick={handlePortalBack} data-testid="workspace-nav-back">
                  Back
                </button>
              </li>
            ) : null}
          </ul>
        </nav>
        <div className="admin-nav-footer">
          <code className="api-base-url" data-testid="api-base-url">
            {config.apiBaseUrl}
          </code>
        </div>
      </aside>
      <main className="admin-main">
        {route === CLINIC_PORTAL_ROUTE ? (
          workspaceMode ? (
            <section className="portal-workspace-shell" data-testid="portal-workspace-shell">
              <div className="portal-workspace-header">
                <p className="portal-eyebrow">{CLINIC_PORTAL_LABEL}</p>
                <h2 data-testid="portal-workspace-heading">{activeWorkspace?.label}</h2>
                <p className="portal-workspace-note">Only one workspace is active at a time.</p>
              </div>
              <ClinicLaunchPanelWithWorkspaceMode workspaceMode={workspaceMode} />
            </section>
          ) : (
            <section className="panel portal-home" data-testid="portal-home">
              <p className="portal-eyebrow">{CLINIC_PORTAL_LABEL}</p>
              <h2>Choose a workspace</h2>
              <p>
                {portalExpanded
                  ? 'Select Clinic Demo Creation or Existing Clinic Demos from the sidebar.'
                  : 'Open Clinic Demo/Payment Portal from the sidebar to choose a workspace.'}
              </p>
            </section>
          )
        ) : null}
      </main>
    </div>
  );
}
